import type { UserManager } from '@skyslope/auth-js';
import type { AxiosRequestConfig } from 'axios';

import type { FormsUserManager } from 'auth/userManager';
import { userManager } from 'auth/userManager';
import { INTEGRATIONS_API_URL } from 'constants/environments';
import LocalStorageItem from 'constants/LocalStorageItem';

import Server from './Server';

export interface IntegrationEnablement {
  enabled: boolean;
  optInAvailable: boolean;
  userEnablement: 'active' | 'inactive' | 'pending';
  brokerEnablement: 'active' | 'inactive' | 'pending';
  serviceStatus?: {
    status: string;
    message?: string;
    code?: number;
  };
  /** Controls when and how integration prompts are shown to users based on their history and preferences */
  promptEligibility?: {
    /** Determines if the UI should display a prompt based on timing, opt-out status, and previous interactions */
    isEligible: boolean;
    /** Explains the eligibility decision with detailed context for debugging and analytics purposes */
    reason: string;
    /** The number of times the user has dismissed the integration prompt */
    dismissCount?: number;
  };
}

export enum IntegrationKey {
  RealSatisfied = 'realsatisfied',
  SellersShield = 'sellersshield',
  UtilityConnect = 'utilityconnect',
}
export type IntegrationsResponse = Partial<{
  [key in IntegrationKey]: IntegrationEnablement;
}> & {
  enableDoNotRemindAgain?: boolean;
};

export interface SellersShieldTokenResponse {
  accessToken: string;
  params: {
    [key: string]: string;
  };
}

export type DismissIntegrationsRequest = {
  integrations: IntegrationKey[];
};

class IntegrationsServer extends Server {
  public constructor(manager: UserManager, axiosConfig: AxiosRequestConfig = {}) {
    super(manager, axiosConfig, {
      tokenGetter: (userManager) => (userManager as FormsUserManager).getAccessToken(),
      includeBaseHeaders: false,
      throwErrorOnInvalidToken: true,
      redirectOn401: false,
    });
  }

  /**
   * Get all integrations for a user
   * @param userId The user ID to get integrations for
   * @returns Response containing enabled integrations
   */
  public async GetUserIntegrations(userId: string): Promise<IntegrationsResponse> {
    function getItem(key: LocalStorageItem) {
      return localStorage.getItem(`${key}-${userId}`);
    }

    function getTimestamp(thing: string | null) {
      return thing ? new Date(thing).getTime() : 0;
    }

    // Get timestamp values for each dismissal, handling null/undefined values
    const sellersShieldTimestamp = getTimestamp(getItem(LocalStorageItem.SellersShieldModalLastDismissed));
    const realsatisfiedTimestamp = getTimestamp(getItem(LocalStorageItem.RealsatisfiedModalLastDismissed));
    const utilityConnectTimestamp = getTimestamp(getItem(LocalStorageItem.UtilityConnectModalLastDismissed));

    // Calculate most recent dismissed date
    const lastPromptedAt = Math.max(sellersShieldTimestamp, realsatisfiedTimestamp, utilityConnectTimestamp);

    // Only include lastPromptedAt if it's greater than 0 (meaning we have at least one valid timestamp)
    const params = new URLSearchParams();
    if (lastPromptedAt > 0) {
      params.append('lastPromptedAt', new Date(lastPromptedAt).toISOString());
      localStorage.removeItem(`${LocalStorageItem.SellersShieldModalLastDismissed}-${userId}`);
      localStorage.removeItem(`${LocalStorageItem.RealsatisfiedModalLastDismissed}-${userId}`);
      localStorage.removeItem(`${LocalStorageItem.UtilityConnectModalLastDismissed}-${userId}`);
    }

    const queryString = params.toString();
    const url = `${INTEGRATIONS_API_URL}/users/${userId}/integrations${queryString ? `?${queryString}` : ''}`;

    return (await this.api.get(url)).data;
  }

  /**
   * Toggle an integration on or off for a user
   * @param integration The integration key to toggle
   * @param userId The user ID to toggle the integration for
   * @returns Updated integration enablement status
   */
  public async ToggleIntegration(integration: IntegrationKey, userId: string): Promise<IntegrationEnablement> {
    return (
      await this.api.post(`${INTEGRATIONS_API_URL}/users/${userId}/integrations`, {
        integration,
      })
    ).data;
  }

  /**
   * Get a Sellers Shield authentication token for the specified user
   * @param userId The user ID to get the token for
   * @returns A response containing the access token and additional params
   */
  public async GetSellersShieldToken(userId: string): Promise<SellersShieldTokenResponse> {
    return (
      await this.api.get<SellersShieldTokenResponse>(`${INTEGRATIONS_API_URL}/sellers-shield/${userId}/auth/token`)
    ).data;
  }

  public async DismissIntegrations(args: {
    userId: string;
    body: DismissIntegrationsRequest;
  }): Promise<IntegrationEnablement[]> {
    const { userId, body } = args;
    return (await this.api.post(`${INTEGRATIONS_API_URL}/users/${userId}/integrations/dismiss`, body)).data;
  }
}

export default new IntegrationsServer(userManager);
